import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { Capacitor } from "@capacitor/core";
import { supabase } from "@/integrations/supabase/client";
import { analytics, appPlatform } from "@/lib/analytics";
import { useLanguage } from "@/i18n/LanguageProvider";
import { Button } from "@/components/ui/button";
import { Sentry } from "@/lib/sentry";
import {
  RESEND_COOLDOWN_MS,
  authCallbackRedirectTo,
  clearPendingConfirmation,
  cooldownRemainingMs,
  cooldownSeconds,
  readPendingConfirmation,
  writePendingConfirmation,
} from "@/lib/emailConfirmation";

/**
 * "Confirme seu e-mail" — a tela que passa a existir entre o cadastro e o app
 * quando o toggle "Confirm email" do Supabase estiver ligado.
 *
 * QUEM CHEGA AQUI: só quem acabou de se cadastrar por E-MAIL e recebeu
 * `session: null` do `signUp`. Google e Apple não passam por aqui — eles
 * devolvem sessão e seguem pelo /auth/callback como sempre.
 *
 * QUEM NUNCA PODE CHEGAR (exigência nº 6): conta já confirmada fazendo login.
 * São TRÊS barreiras independentes, porque uma só seria uma promessa:
 *   1. `decideSignupOutcome` testa "conta já existe" ANTES de "sem sessão", e
 *      o login por senha nem passa perto deste ramo (Auth.tsx trata
 *      signInWithPassword em outro braço);
 *   2. esta tela verifica a sessão na montagem e sai sozinha se houver uma;
 *   3. sem e-mail pendente ela não tem o que mostrar e volta para /auth.
 *
 * O e-mail vem do state de navegação e, se a pessoa recarregar a página, do
 * depósito em localStorage — que também é onde mora a trava de reenvio, senão
 * um F5 zeraria o contador e a trava seria enfeite.
 */
export default function ConfirmEmail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, tf } = useLanguage();

  const stateEmail = (location.state as { email?: string } | null)?.email;
  const [email, setEmail] = useState<string | null>(stateEmail ?? null);
  const [remaining, setRemaining] = useState(RESEND_COOLDOWN_MS);
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(true);

  // ── Barreira 2 — quem já tem sessão não fica aqui ─────────────────────────
  // Vale para o usuário que confirmou em outra aba e voltou nesta, e para
  // qualquer navegação direta à URL.
  useEffect(() => {
    let active = true;
    void supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      if (data.session) {
        clearPendingConfirmation();
        navigate("/auth/callback", { replace: true });
        return;
      }
      setChecking(false);
    });
    return () => { active = false; };
  }, [navigate]);

  // Confirmou em outra aba (ou o deep link nativo entregou a sessão enquanto
  // esta tela estava aberta): sai na hora, sem pedir nada.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!session) return;
      if (event === "SIGNED_IN" || event === "INITIAL_SESSION" || event === "TOKEN_REFRESHED") {
        clearPendingConfirmation();
        navigate("/auth/callback", { replace: true });
      }
    });
    return () => subscription.unsubscribe();
  }, [navigate]);

  // ── Barreira 3 — sem e-mail pendente não há tela ──────────────────────────
  // Semeia o depósito quando a pessoa chegou pelo state de navegação (primeira
  // vez) e o recupera quando ela recarregou (state perdido).
  const semeadoRef = useRef(false);
  useEffect(() => {
    if (semeadoRef.current) return;
    semeadoRef.current = true;
    const pendente = readPendingConfirmation();
    if (stateEmail) {
      const jaTinha = pendente?.email === stateEmail ? pendente : null;
      const registro = jaTinha ?? {
        email: stateEmail,
        resendableAt: Date.now() + RESEND_COOLDOWN_MS,
      };
      writePendingConfirmation(registro);
      setEmail(registro.email);
      setRemaining(cooldownRemainingMs(registro, Date.now()));
      return;
    }
    if (pendente) {
      setEmail(pendente.email);
      setRemaining(cooldownRemainingMs(pendente, Date.now()));
      return;
    }
    navigate("/auth", { replace: true });
  }, [stateEmail, navigate]);

  // Contador. Relê o depósito a cada tick em vez de descontar de um número em
  // memória: assim o valor continua certo depois de a aba ficar suspensa em
  // segundo plano, onde os timers não rodam na cadência que se supõe.
  useEffect(() => {
    const id = window.setInterval(() => {
      setRemaining(cooldownRemainingMs(readPendingConfirmation(), Date.now()));
    }, 500);
    return () => window.clearInterval(id);
  }, []);

  const handleResend = useCallback(async () => {
    if (!email || sending) return;
    if (cooldownRemainingMs(readPendingConfirmation(), Date.now()) > 0) return;
    setSending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email,
        options: {
          emailRedirectTo: authCallbackRedirectTo({
            native: Capacitor.isNativePlatform(),
            origin: typeof window !== "undefined" ? window.location.origin : null,
          }),
        },
      });
      if (error) throw error;
      // Rearma a trava ANTES de avisar: se a pessoa tocar duas vezes rápido, o
      // segundo toque já encontra a janela fechada.
      const registro = { email, resendableAt: Date.now() + RESEND_COOLDOWN_MS };
      writePendingConfirmation(registro);
      setRemaining(RESEND_COOLDOWN_MS);
      toast.success(t("auth.confirmEmail.resent"));
      analytics.signupEmailConfirmationResent(appPlatform());
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message ?? "";
      console.error("[ConfirmEmail] resend failed:", err);
      toast.error(msg || t("auth.confirmEmail.resendError"));
      // Mesmo falhando, fecha a janela: um 429 do servidor respondido com
      // botão liberado convida a bater de novo e tomar outro 429.
      writePendingConfirmation({ email, resendableAt: Date.now() + RESEND_COOLDOWN_MS });
      setRemaining(RESEND_COOLDOWN_MS);
      try {
        Sentry.captureException(err instanceof Error ? err : new Error(msg || "resend failed"), {
          tags: { feature: "auth_confirm_email_resend_failed" },
        });
      } catch { /* Sentry not initialised in dev */ }
    } finally {
      setSending(false);
    }
  }, [email, sending, t]);

  // Saída para quem errou o endereço. Limpa o pendente e volta ao /auth — a
  // conta criada com o e-mail errado fica sem confirmar e nunca vira sessão.
  const handleWrongEmail = useCallback(() => {
    clearPendingConfirmation();
    navigate("/auth", { replace: true });
  }, [navigate]);

  if (checking || !email) {
    return (
      <div
        className="fixed inset-0 flex items-center justify-center"
        style={{ background: "linear-gradient(135deg, #050811 0%, #0a0e1a 50%, #050811 100%)" }}
      >
        <Loader2 className="w-8 h-8 animate-spin text-cyan-300/80" />
      </div>
    );
  }

  const segundos = cooldownSeconds(remaining);
  const travado = segundos > 0 || sending;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center px-5"
      style={{ background: "linear-gradient(135deg, #050811 0%, #0a0e1a 50%, #050811 100%)" }}
    >
      <div
        className="w-full max-w-sm rounded-2xl p-6 ring-1 ring-white/10"
        style={{ background: "linear-gradient(135deg, #0a0e1a 0%, #0f1419 50%, #0a0e1a 100%)" }}
        role="region"
        aria-labelledby="confirm-email-title"
      >
        <div className="flex items-center gap-3 mb-4">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: "rgba(34,211,238,0.10)", border: "0.5px solid rgba(34,211,238,0.30)" }}
          >
            <MailCheck size={18} className="text-cyan-300" aria-hidden="true" />
          </div>
          <h1
            id="confirm-email-title"
            className="font-display text-lg font-semibold text-foreground"
          >
            {t("auth.confirmEmail.title")}
          </h1>
        </div>

        {/* O ENDEREÇO, visível. É o que deixa o erro de digitação óbvio antes
            de a pessoa ficar esperando um e-mail que nunca chega. */}
        <p className="text-muted-foreground text-sm leading-relaxed mb-2">
          {tf("auth.confirmEmail.body", { email })}
        </p>
        <p className="text-muted-foreground/70 text-[12px] leading-relaxed mb-5">
          {t("auth.confirmEmail.hint")}
        </p>

        <Button
          variant="hero"
          size="xl"
          className={`w-full ${travado ? "opacity-50 cursor-not-allowed" : ""}`}
          onClick={handleResend}
          disabled={travado}
          aria-disabled={travado}
        >
          {sending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : segundos > 0 ? (
            tf("auth.confirmEmail.resendIn", { seconds: String(segundos) })
          ) : (
            t("auth.confirmEmail.resend")
          )}
        </Button>

        <button
          type="button"
          onClick={handleWrongEmail}
          className="mt-3 w-full text-center text-[12px] font-body text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus:underline"
        >
          {t("auth.confirmEmail.wrongEmail")}
        </button>
      </div>
    </div>
  );
}
